import * as React from "react";
import { isAxiosError } from "axios";
import { useReviewSessionNoteWithAi } from "../hooks/mutations/note";
import { IAiSessionNoteReview } from "../modules/notes/types/notes.types";

export type AiSessionNoteReviewPhase =
    | "idle"
    | "saving"
    | "reviewing"
    | "error";

export type BeforeAiSessionNoteReview = () => Promise<boolean>;

interface AiSessionNoteReviewContextValue {
    noteId: string;
    review: IAiSessionNoteReview | null;
    phase: AiSessionNoteReviewPhase;
    errorMessage: string | null;
    isStale: boolean;
    focusRequestKey: number;
    isPending: boolean;
    runReview(
        beforeReview?: BeforeAiSessionNoteReview,
        onAccessDenied?: () => void
    ): Promise<IAiSessionNoteReview | null>;
    dismissReview(): void;
    markReviewStale(): void;
}

const AiSessionNoteReviewContext =
    React.createContext<AiSessionNoteReviewContextValue | null>(null);

function getReviewErrorMessage(error: unknown): string {
    if (isAxiosError<{ message?: string }>(error)) {
        return (
            error.response?.data?.message ??
            "Could not review this note. No note content was changed."
        );
    }
    return "Could not review this note. No note content was changed.";
}

export function AiSessionNoteReviewProvider({
    noteId,
    children,
}: React.PropsWithChildren<{ noteId: string }>) {
    const { mutateAsync: reviewNote } = useReviewSessionNoteWithAi(noteId);
    const [review, setReview] = React.useState<IAiSessionNoteReview | null>(
        null
    );
    const [phase, setPhase] = React.useState<AiSessionNoteReviewPhase>("idle");
    const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
    const [isStale, setIsStale] = React.useState(false);
    const [focusRequestKey, setFocusRequestKey] = React.useState(0);
    const activeRequestRef = React.useRef(0);
    const activeNoteIdRef = React.useRef(noteId);
    activeNoteIdRef.current = noteId;

    React.useEffect(() => {
        activeRequestRef.current += 1;
        setReview(null);
        setPhase("idle");
        setErrorMessage(null);
        setIsStale(false);
        setFocusRequestKey(0);
        return () => {
            activeRequestRef.current += 1;
        };
    }, [noteId]);

    const runReview = React.useCallback(
        async (
            beforeReview?: BeforeAiSessionNoteReview,
            onAccessDenied?: () => void
        ) => {
            const requestKey = activeRequestRef.current + 1;
            activeRequestRef.current = requestKey;
            const requestNoteId = noteId;
            setErrorMessage(null);
            setFocusRequestKey((current) => current + 1);
            let reviewStarted = false;

            try {
                if (beforeReview) {
                    setPhase("saving");
                    const saved = await beforeReview();
                    if (
                        activeRequestRef.current !== requestKey ||
                        activeNoteIdRef.current !== requestNoteId
                    ) {
                        return null;
                    }
                    if (!saved) {
                        setPhase("error");
                        setErrorMessage(
                            "Save the note before starting the Mantle AI review."
                        );
                        return null;
                    }
                }

                setPhase("reviewing");
                reviewStarted = true;
                const response = await reviewNote();
                if (
                    activeRequestRef.current !== requestKey ||
                    activeNoteIdRef.current !== requestNoteId
                ) {
                    return null;
                }
                setReview(response.data);
                setIsStale(false);
                setPhase("idle");
                return response.data;
            } catch (error: unknown) {
                if (
                    activeRequestRef.current !== requestKey ||
                    activeNoteIdRef.current !== requestNoteId
                ) {
                    return null;
                }
                if (
                    reviewStarted &&
                    isAxiosError(error) &&
                    error.response?.status === 403 &&
                    onAccessDenied
                ) {
                    setReview(null);
                    setIsStale(false);
                    setPhase("idle");
                    onAccessDenied();
                    return null;
                }
                setPhase("error");
                setErrorMessage(getReviewErrorMessage(error));
                return null;
            }
        },
        [noteId, reviewNote]
    );

    const dismissReview = React.useCallback(() => {
        activeRequestRef.current += 1;
        setReview(null);
        setPhase("idle");
        setErrorMessage(null);
        setIsStale(false);
    }, []);

    const markReviewStale = React.useCallback(() => {
        setIsStale((current) => current || review !== null);
    }, [review]);

    const value = React.useMemo<AiSessionNoteReviewContextValue>(
        () => ({
            noteId,
            review,
            phase,
            errorMessage,
            isStale,
            focusRequestKey,
            isPending: phase === "saving" || phase === "reviewing",
            runReview,
            dismissReview,
            markReviewStale,
        }),
        [
            dismissReview,
            errorMessage,
            focusRequestKey,
            isStale,
            noteId,
            phase,
            review,
            runReview,
            markReviewStale,
        ]
    );

    return (
        <AiSessionNoteReviewContext.Provider value={value}>
            {children}
        </AiSessionNoteReviewContext.Provider>
    );
}

export function AiSessionNoteReviewBoundary({
    noteId,
    children,
}: React.PropsWithChildren<{ noteId: string }>) {
    const existingContext = React.useContext(AiSessionNoteReviewContext);
    if (existingContext && existingContext.noteId === noteId) {
        return children;
    }
    return (
        <AiSessionNoteReviewProvider key={noteId} noteId={noteId}>
            {children}
        </AiSessionNoteReviewProvider>
    );
}

export function useAiSessionNoteReview(): AiSessionNoteReviewContextValue {
    const context = React.useContext(AiSessionNoteReviewContext);
    if (!context) {
        throw new Error(
            "AI session note review requires a note-scoped provider."
        );
    }
    return context;
}

export function useOptionalAiSessionNoteReview(): AiSessionNoteReviewContextValue | null {
    return React.useContext(AiSessionNoteReviewContext);
}
